<template>
  <b-layout :colors="$site.colors" :header-title="$t('pages.book.confirm.title')" :header-back-route="backRoute" :breadcrumbs="breadcrumbs" :notice="false">
    <b-container class="mt-12">
      <b-summary>
        <template #summary>
          <b-card
            :title="$t('pages.book.confirm.bookingDetails')"
            class="mb-4"
          >
            <move-detail v-if="moveReservation" />

            <div v-if="selectedCustomer" class="mb-8">
              <div class="flex items-center justify-between mb-1">
                <h6 class="font-semibold text-xs uppercase text-secondary/60">
                  {{ $t('general.customer') }}
                </h6>

                <nuxt-link :to="customerRoute" class="text-secondary/60">
                  <b-icon icon="edit" base-class="h-4 w-4" />
                </nuxt-link>
              </div>
              <div>
                <div class="flex items-start justify-between text-sm text-secondary">
                  <div>
                    <p class="font-semibold">
                      {{ selectedCustomer.displayName }}
                    </p>
                    <p>
                      {{ selectedCustomer.email }}
                    </p>
                  </div>

                  <div v-if="customerSegments" class="flex items-center justify-center space-x-2 mt-3">
                    <b-tooltip
                      v-for="segment in customerSegments"
                      :key="segment.idSegment"
                      :text="segment.segment.name"
                    >
                      <b-chip :colour="segment.segment.hexColour">
                        {{ segment.segment.shortName || segment.segment.name }}
                      </b-chip>
                    </b-tooltip>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="selectedSlot" class="mb-8">
              <div class="flex items-center justify-between mb-1">
                <h6 class="font-semibold text-xs uppercase text-secondary/60">
                  {{ $t('general.services') }}
                </h6>

                <nuxt-link :to="servicesRoute" class="text-secondary/60">
                  <b-icon icon="edit" base-class="h-4 w-4" />
                </nuxt-link>
              </div>
              <div>
                <div v-for="(slotService, serviceIndex) in selectedSlot.services" :key="`slotService-`+ serviceIndex" class="flex items-start justify-between text-sm text-secondary">
                  <div>
                    <p class="font-semibold">
                      {{ slotService.service.title }}
                    </p>
                    <p>
                      {{ $t('general.withEmployee', {employee: slotService.employee.firstName}) }}

                      <span v-if="slotService.resources.length > 0" class="mt-1 opacity-80">
                        (<span v-for="(resource, resourceIndex) in slotService.resources" :key="`service-${serviceIndex}-resource-${resourceIndex}`">
                          {{ resource.serviceResource.displayLabel }}
                          <template v-if="resourceIndex < slotService.resources.length - 1">,</template>
                        </span>)
                      </span>
                    </p>
                  </div>

                  <div>
                    <p v-if="$site.settings.bookShowServicePrices && slotService.price">
                      {{ priceLabelForSlotService(slotService) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="selectedSlot" class="mb-6">
              <div class="flex items-center justify-between mb-1">
                <h6 class="font-semibold text-xs uppercase text-secondary/60">
                  {{ $t('general.dateAndTime') }}
                </h6>

                <nuxt-link :to="availabilityRoute" class="text-secondary/60">
                  <b-icon icon="edit" base-class="h-4 w-4" />
                </nuxt-link>
              </div>

              <div>
                <div class="flex items-start justify-between text-sm text-secondary">
                  <p>
                    {{ $format.date(selectedSlot.date) }} @ {{ selectedSlot.startTimeLabel }}
                  </p>
                </div>
              </div>
            </div>

            <div v-if="response && response.payment">
              <b-alert v-if="response.payment.depositRequired && response.payment.options" variant="success">
                <b-html :html="response.payment.depositRequiredMessage" />
              </b-alert>

              <b-alert v-if="response.payment.captureRequired && response.payment.options" variant="success">
                <b-html :html="response.payment.captureRequiredMessage" />
              </b-alert>
            </div>
          </b-card>
        </template>

        <div>
          <!--<b-alert :title="$t('pages.book.confirm.notFinished')" variant="warning" class="mb-3">
            <p>{{ $t('pages.book.confirm.toFinaliseBooking') }}</p>
          </b-alert>-->

          <b-card v-if="errors && errors.length" :title="$t('general.correctErrors')" class="mb-3" variant="danger">
            <ul>
              <li v-for="error in errors" class="text-white" :key="error">
                {{ error }}
              </li>
            </ul>
          </b-card>

          <div v-if="initialLoad && !response">
            <b-card
              class="mb-3 text-center "
            >
              <b-spinner label="Loading" type="grow" class="m-2 bookapp-action-tint-color" />
            </b-card>
          </div>

          <div v-else-if="response">
            <b-card
              :title="$t('pages.book.confirm.notFinished')"
              :subtitle="$t('pages.book.confirm.toFinaliseBooking')"
              class="mb-6"
            >
              <form @submit.prevent="submit">
                <div class="sm:grid grid-cols-2 gap-3">
                  <UFormGroup
                    v-for="field in primaryFields"
                    :key="field.field"
                    :ref="`field_` + field.field"
                    :class="fieldColumns(field, primaryFields)"
                    :label="!field.custom ? $t('general.'+field.field) : field.label"
                    :description="field.field === 'notes' ? $t('general.notesInfo') : ''"
                    :required="field.required"
                  >
                    <ElementsDynamicField
                      v-if="field.custom"
                      v-model="submission.customFields[field.customField.id]"
                      :field="field"
                      :disabled="submission.locked"
                    />

                    <ElementsDynamicField
                      v-else
                      v-model="submission.fields[field.field]"
                      :field="field"
                      :disabled="submission.locked"
                    />
                  </UFormGroup>
                </div>

                <b-button v-if="secondaryFields.length > 0 && !showMoreFields" class="w-full my-3" @click="showMoreFieldsClicked">
                  {{ $t('general.more') }}
                </b-button>

                <div v-if="secondaryFields.length > 0 && showMoreFields" class="sm:grid grid-cols-2 gap-3">
                  <UFormGroup
                    v-for="field in secondaryFields"
                    :key="field.field"
                    :ref="`field_` + field.field"
                    :class="fieldColumns(field, secondaryFields)"
                    :label="!field.custom ? $t('general.'+field.field) : field.label"
                    :required="field.required"
                  >
                    <ElementsDynamicField
                      v-if="field.custom"
                      v-model="submission.customFields[field.customField.id]"
                      :field="field"
                      :disabled="submission.locked"
                    />

                    <ElementsDynamicField
                      v-else
                      v-model="submission.fields[field.field]"
                      :field="field"
                      :disabled="submission.locked"
                    />
                  </UFormGroup>
                </div>
              </form>
            </b-card>

            <b-card
              v-if="response.terms.required"
              ref="terms"
              :title="response.terms.title"
              class="mb-6"
            >
              <b-html :html="response.terms.content" class="mb-4 text-sm text-secondary" />

              <BCheckbox v-model="submission.termsAccepted" :disabled="submission.locked" :label="response.terms.acceptLabel" />
            </b-card>

            <b-card
              v-if="response.giftCardVoucherRequest.showField"
              :title="$t('pages.book.confirm.voucherGiftCard')"
              class="mb-6"
            >
              <b-alert v-if="response.giftCardVoucherRequest.showVoucherLink || (response.giftCardVoucherRequest.errors && response.giftCardVoucherRequest.errors.length)" variant="danger" class="mb-3">
                <ul v-if="response.giftCardVoucherRequest.errors && response.giftCardVoucherRequest.errors.length" class="bookapp-errors">
                  <li v-for="error in response.giftCardVoucherRequest.errors" :key="error">
                    {{ error }}
                  </li>
                </ul>

                <div v-if="response.giftCardVoucherRequest.showVoucherLink">
                  <small>
                    {{ $t('pages.book.confirm.servicesForThisVoucher') }}
                    <nuxt-link :to="{ path: '/book-now', query: { voucher: response.giftCardVoucherRequest.code } }" class="text-white">
                      <u>{{ $t('pages.book.confirm.clickHere') }}</u>
                    </nuxt-link>
                  </small>
                </div>
              </b-alert>

              <form @submit.prevent="voucherRequest">
                <UFormGroup :prepend="$t('general.code')">
                  <div class="w-full sm:flex items-center space-x-2">
                    <UInput v-model="sanitisedGiftCardVoucherCode" size="lg" class="mb-2 sm:mb-0 w-full" :ui="{ leading: { padding: { lg: 'ps-20'}}}">
                      <template #leading>
                        <p class="text-secondary/50 mr-3 pr-3 border-r border-secondary">
                          {{ $t('general.code') }}
                        </p>
                      </template>
                    </UInput>

                    <UButton block class="block sm:w-auto flex-shrink-0 text-sm" type="submit" size="lg" :loading="giftCardVoucherLoading">
                      {{ $t('pages.book.confirm.applyVoucherGiftCard') }}
                    </UButton>
                  </div>
                </UFormGroup>
              </form>

              <div v-if="response.voucher || (response.payment.giftCards && response.payment.giftCards.length > 0)">
                <template v-if="response.voucher">
                  <div class="mt-3 mb-1 w-full flex items-center justify-between text-secondary p-3 rounded bg-secondary text-sm">
                    <div class="flex items-center space-x-3  rtl:space-x-reverse">
                      <b-icon icon="voucher" class="text-primary" />
                      <p><span class="font-semibold">{{ $t('general.voucher') }}:</span>{{ response.voucher.name }}</p>
                    </div>

                    <div class="flex items-center space-x-3  rtl:space-x-reverse">
                      <p><span class="font-semibold">{{ $t('general.discount') }}: </span>{{ response.voucher.discountLabel }}</p>

                      <a class="cursor-pointer hover:text-primary" @click="removeVoucher">
                        <b-icon icon="x" />
                      </a>
                    </div>
                  </div>
                </template>

                <template v-if="response.payment.giftCards">
                  <div v-for="card in response.payment.giftCards" :key="card.number" class="mt-3 mb-1 w-full flex items-center justify-between text-secondary p-3 rounded bg-secondary text-sm">
                    <div class="flex items-center space-x-3  rtl:space-x-reverse">
                      <b-icon icon="voucher" class="text-primary" />
                      <p><span class="font-semibold">{{ $t('general.giftcard') }}:</span>{{ card.displayNumber }}</p>
                    </div>

                    <div class="flex items-center space-x-3  rtl:space-x-reverse">
                      <p><span class="font-semibold">{{ $t('general.balance') }}: </span>{{ card.balanceLabel }}</p>

                      <a class="cursor-pointer hover:text-primary" @click="removeGiftCard(card)">
                        <b-icon icon="x" />
                      </a>
                    </div>
                  </div>
                </template>
              </div>
            </b-card>

            <b-card
              v-if="response.payment && response.payment.options"
              :title="response.payment.options.length > 1 || !selectedPaymentOption ? $t('pages.book.confirm.payment') : ''"
              content-class=""
              class="mb-6"
            >
              <b-alert v-if="errors && errors.length > 0" variant="danger" class="mb-3">
                <ul class="bookapp-errors">
                  <li v-for="error in errors" :key="error">
                    {{ error }}
                  </li>
                </ul>
              </b-alert>

              <div>
                <div v-if="response.payment.options.length > 1" class="grid grid-cols-2 gap-3">
                  <button
                    v-for="option in response.payment.options"
                    :key="option.code"
                    :class="[
                      'shadow rounded cursor-pointer hover:bg-action focus:bg-action hover:scale-105 group transition-all relative mb-2 w-full text-left',
                      option == selectedPaymentOption ? 'bg-action' : 'bg-primary/80'
                    ]"
                    @click="selectPaymentOption(option, true)"
                  >
                    <div class="flex items-center justify-between border-b p-4">
                      <span
                        :class="[
                          'text-sm transition-all',
                          option == selectedPaymentOption ? 'text-action' : 'text-secondary group-hover:text-action group-focus:text-action'
                        ]"
                      >
                        <h6 slot="header" class="mb-0">
                          {{ $t(`pages.book.payments.${option.titleCode}`, { paymentItemName: option.paymentItemName }) }}
                        </h6>
                      </span>

                      <div v-if="option != selectedPaymentOption" class="rounded-full h-6 w-6 flex items-center justify-center border" />

                      <b-icon v-if="option == selectedPaymentOption" icon="checkCircle" base-class="h-7 w-7 text-action" />
                    </div>
                    <div class="p-4">
                      <span
                        :class="[
                          'text-lg font-semibold transition-all',
                          option == selectedPaymentOption ? 'text-action' : 'text-primary group-hover:text-action group-focus:text-action'
                        ]"
                      >
                        {{ option.subtitle }}
                      </span>
                    </div>

                    <div v-if="selectingPaymentOptionCode === option.code" class="z-10 absolute inset-0 h-full w-full bg-primary bg-opacity-50 flex items-center justify-center">
                      <b-spinner />
                    </div>
                  </button>
                </div>

                <div v-if="response.payment.options.length === 1">
                  <template v-if="!selectedPaymentOption">
                    <button
                      class="shadow rounded-md cursor-pointer bg-action hover:bg-action focus:bg-action hover:scale-105 group transition-all relative mb-2 w-full text-center p-3 block"
                      @click="selectPaymentOption(response.payment.options[0], true)"
                    >
                      <h6 class="text-sm transition-all text-action">
                        {{ $t(`pages.book.payments.${response.payment.options[0].titleCode}`, { paymentItemName: response.payment.options[0].paymentItemName }) }}
                      </h6>
                      <span class="text-lg font-semibold transition-all text-action">
                        {{ response.payment.options[0].subtitle }}
                      </span>
                    </button>
                  </template>

                  <template v-if="selectedPaymentOption">
                    <h4 class="text-lg font-semibold text-primary">
                      {{ selectedPaymentOption.subtitle || $t(`pages.book.payments.${selectedPaymentOption.titleCode}`, { paymentItemName: selectedPaymentOption.paymentItemName }) }}
                    </h4>
                    <p v-if="selectedPaymentOption.subtitle" class="text-sm text-secondary/70">
                      {{ $t(`pages.book.payments.${selectedPaymentOption.titleCode}`, { paymentItemName: selectedPaymentOption.paymentItemName }) }}
                    </p>
                  </template>
                </div>

                <div v-if="selectedPaymentOption">
                  <div v-if="selectedPaymentOption.splitPayments && selectedPaymentOption.splitPayments.payments.length" class="mt-8">
                    <h6 class="text-primary font-semibold mb-3">
                      {{ $t('general.payments') }}
                    </h6>

                    <div v-for="(payment, index) in selectedPaymentOption.splitPayments.payments" :key="index" class="mt-3 mb-1 w-full flex items-center justify-between text-secondary p-4 rounded bg-primary text-sm">
                      <div class="flex items-center space-x-3  rtl:space-x-reverse">
                        <b-icon icon="voucher" class="text-primary" />
                        <p><span class="font-semibold">{{ payment.typeLabel }}:</span>{{ payment.label }}</p>
                      </div>

                      <div class="flex items-center space-x-3  rtl:space-x-reverse">
                        <p class="font-semibold">
                          {{ payment.amountLabel }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <template v-if="!response.terms.required || submission.termsAccepted">
                    <div v-if="selectedPaymentOption.methods" class="mt-8">
                      <PaymentForm
                        :confirm-button-title="$t(`pages.book.button.${selectedPaymentOption.confirmButtonTitleCode}`)"
                        :methods="selectedPaymentOption.methods"
                        :label="selectedPaymentOption.title"
                        :amount="selectedPaymentOption.amountRequired"
                        :amount-label="selectedPaymentOption.amountRequiredLabel"
                        :intent="selectedPaymentOption.intent"
                        :customer="selectedCustomer"
                        :fallback-route="externalFallbackRouteCallback"
                        :action="actionCallback"
                        :processing="processingPayment"
                        @callback="paymentCallback"
                      />
                    </div>
                    <div v-else>
                      <b-button variant="action" class="w-full mt-6" :loading="submitting" :disabled="submitting" @click="submit">
                        {{ $t(`pages.book.button.${selectedPaymentOption.confirmButtonTitleCode}`) }}
                      </b-button>
                    </div>
                  </template>
                </div>
              </div>
            </b-card>
            <div v-else>
              <b-button variant="action" class="w-full mt-6" :loading="submitting" :disabled="submitting" @click="submit">
                {{ $t('general.confirm') }}
              </b-button>
            </div>
          </div>
        </div>
      </b-summary>
    </b-container>
  </b-layout>
</template>

<script>
import { Buffer } from 'buffer';

import * as Fields from '@/components/fields'
import PaymentForm from '@/components/payments/PaymentForm'
import MoveDetail from '@/components/booking/elements/MoveDetail.vue'

import { BookingStage, createBookingRoute } from '@/components/booking/elements/Detail.vue'
import Utils from '@/utils'

definePageMeta({
  tabs: ['online'],
  middleware: ['redirects']
});

export default defineNuxtComponent({
  components: {
    PaymentForm,
    MoveDetail
  },
  data () {
    return {
      errors: [],
      response: null,
      initialLoad: true,
      selectedCustomer: null,
      selectedSlot: null,
      moveReservation: null,
      rebookReservation: null,
      selectedPaymentOption: null,
      selectingPaymentOptionCode: null,
      submitting: false,
      showMoreFields: false,
      giftCardVoucherCode: null,
      giftCardVoucherLoading: false,
      processingPayment: false,
      submission: {
        locked: false,
        fields: {},
        customFields: {},
        termsAccepted: false,
        voucher: null,
        payment: {
          data: null,
          giftCards: []
        }
      }
    }
  },
  async asyncData () {
    const route = useRoute()
    const bookingStore = useBookingStore()

    await bookingStore.setFromUrlQuery(route.query)

    return {}
  },
  computed: {
    backRoute () {
      const bookingUrlQuery = this.$store.getters['booking/urlQuery']

      return createBookingRoute(bookingUrlQuery, BookingStage.CUSTOMER, { override: true })
    },
    breadcrumbs () {
      const bookingUrlQuery = this.$store.getters['booking/urlQuery']

      return [
        { text: this.$site.tabs.getName('book', 'Book'), to: createBookingRoute(bookingUrlQuery, BookingStage.SERVICES) },
        { text: this.$t('pages.book.availability.title'), to: createBookingRoute(bookingUrlQuery, BookingStage.AVAILABILITY) },
        { text: this.$t('pages.book.customer.title'), to: createBookingRoute(bookingUrlQuery, BookingStage.CUSTOMER, { override: true }) },
        { text: this.$t('pages.book.confirm.title'), active: true }
      ]
    },
    primaryFields () {
      if (this.response && this.response.fields) {
        return this.response.fields.filter((field) => {
          return (field.show && field.primary)
        })
      }
      return []
    },
    secondaryFields () {
      if (this.response && this.response.fields) {
        return this.response.fields.filter((field) => {
          return (field.show && !field.primary)
        })
      }
      return []
    },
    servicesRoute () {
      const bookingUrlQuery = this.$store.getters['booking/urlQuery']

      return createBookingRoute(bookingUrlQuery, BookingStage.SERVICES)
    },
    availabilityRoute () {
      const bookingUrlQuery = this.$store.getters['booking/urlQuery']

      return createBookingRoute(bookingUrlQuery, BookingStage.AVAILABILITY)
    },
    customerRoute () {
      const bookingUrlQuery = this.$store.getters['booking/urlQuery']

      return createBookingRoute(bookingUrlQuery, BookingStage.CUSTOMER, { override: true })
    },
    voucher () {
      return this.$store.getters['booking/voucher']
    },
    giftCard () {
      return this.$store.getters['booking/giftCard']
    },
    waitlist () {
      return this.$store.getters['booking/waitlist']
    },
    customerSegments () {
      const selectedCustomer = this.selectedCustomer
      const authenticatedCustomer = this.$auth.user.value

      if (authenticatedCustomer && selectedCustomer?.id === authenticatedCustomer?.id) {
        return authenticatedCustomer.customerSegments
      }
    },
    sanitisedGiftCardVoucherCode: {
      get() {
        return this.giftCardVoucherCode?.replace(/#/g, '');
      },
      set(value) {
        this.giftCardVoucherCode = value.replace(/#/g, '');
      },
    },
  },
  created () {
    this.selectedCustomer = this.$store.getters['booking/selectedCustomer']
    this.selectedSlot = this.$store.getters['booking/selectedSlot']
    this.moveReservation = this.$store.getters['booking/moveReservation']
    this.rebookReservation = this.$store.getters['booking/rebookReservation']

    if (this.voucher) {
      this.giftCardVoucherCode = this.voucher.code
    }

    if (this.giftCard) {
      this.giftCardVoucherCode = this.giftCard
    }

    const query = this.$route.query
    if (query && query.submission) {
      const submission = JSON.parse(Buffer.from(query.submission, 'base64').toString())

      if (submission) {
        // vue.set(this, 'submission', submission)
        this.submission = submission
      }
    }
  },
  mounted () {
    this.loadData()
  },
  beforeDestroy () {
    clearInterval(this.paymentInterval)
  },
  methods: {
    priceLabelForSlotService (slotService) {
      return (slotService.service.priceFrom ? (this.$t('general.from') + ' ') : '') + slotService.priceLabel
    },
    formatHtml (text) {
      return Utils.text.formatHtml(text)
    },
    showMoreFieldsClicked () {
      this.showMoreFields = true
    },
    fieldColumns (field) {
      if (field.type === 'textarea') {
        return 'col-span-2'
      } else {
        return ''
      }
    },
    fieldComponent (field) {
      if (field.type === 'text') {
        return Fields.TextField
      } else if (field.type === 'textarea') {
        return Fields.TextAreaField
      } else if (field.type === 'list') {
        return Fields.ListField
      } else if (field.type === 'checkbox') {
        return Fields.ToggleField
      } else if (field.type === 'date') {
        return Fields.DateField
      } else {
        return Fields.TextField
      }
    },
    fieldComponentProps (field) {
      if (field.type === 'list') {
        if (field.custom && field.customField) {
          const options = {}
          for (let j = 0; j < field.customField.multipleValues.length; j++) {
            const option = field.customField.multipleValues[j]
            options[option] = option
          }

          return {  options }
        } else {
          return { options: field.options }
        }
      } else if (field.type === 'date') {
        return {
          augmenter: 'timestamp'
        }
      }

      return null
    },
    removeVoucher () {
      this.submission.voucher = null
      this.giftCardVoucherCode = null

      this.$store.dispatch('booking/setVoucher', null)

      const query = Object.assign({}, this.$route.query)
      delete query.voucher
      this.$router.replace({ query })

      this.loadData()
    },
    removeGiftCard (giftCard) {
      for (let i = 0; i < this.submission.payment.giftCards.length; i++) {
        if (this.submission.payment.giftCards[i] === giftCard) {
          this.submission.payment.giftCards.splice(i, 1)
        }
      }

      this.loadData()
    },
    async voucherRequest () {
      this.giftCardVoucherLoading = true
      await this.loadData()
      this.giftCardVoucherLoading = false
    },
    async loadData () {
      try {
        const response = await this.$api.booking.confirm(this.$api.booking.ACTION_INFO, this.selectedCustomer, this.selectedSlot, { moveReservation: this.moveReservation, rebookReservation: this.rebookReservation, voucher: this.submission.voucher, giftCards: this.submission.payment.giftCards, giftCardVoucherCode: this.giftCardVoucherCode, waitlist: this.waitlist })

        if (response.success) {
          this.errors = []
          this.response = response
          this.didLoad()
        } else if (response.errors) {
          this.errors = response.errors
        }
      } catch (error) {
        console.log(error)
        this.errors.push(this.$t('general.errorBooking'))
        // this.$sentry.captureException(error)
      }

      this.initialLoad = false
    },
    didLoad () {
      this.giftCardVoucherCode = (this.voucher) ? this.voucher.code : null

      this.submission.voucher = this.response.voucher
      this.submission.payment.giftCards = this.response.payment.giftCards

      if (this.response.fields) {
        for (let i = 0; i < this.response.fields.length; i++) {
          const field = this.response.fields[i]

          if (field.custom && field.customField) {
            if (this.submission.customFields[field.customField.id] === undefined) {
              const prefill = null // this.$prefill.getCustomValue('reservation', field.customField.code, field)
              if (prefill !== null) {
                // vue.set(this.submission.customFields, field.customField.id, prefill)
                this.submission.customFields[field.customField.id] = prefill
              } else {
                // vue.set(this.submission.customFields, field.customField.id, field.customField.defaultValue)
                this.submission.customFields[field.customField.id] = field.customField.defaultValue
              }
            }
          } else {
            const prefill = null // this.$prefill.getValue('reservation', field.field, field)
            if (prefill !== null) {
              this.submission.fields[field.field] = prefill
            }
          }
        }
      }

      if (this.submission.payment.data && this.response.payment.options) {
        for (let i = 0; i < this.response.payment.options.length; i++) {
          const option = this.response.payment.options[i]

          if (this.submission.payment.data === option.data) {
            this.selectPaymentOption(option, true)
          } else if (option.methods) {
            for (let j = 0; j < option.methods.length; j++) {
              const method = option.methods[j]
              if (this.submission.payment.data === method.data) {
                this.selectPaymentOption(option, true)
              }
            }
          }
        }
      }

      if (this.selectedPaymentOption) {
        for (let i = 0; i < this.response.payment.options.length; i++) {
          const option = this.response.payment.options[i]

          if (option && this.selectedPaymentOption && option.code === this.selectedPaymentOption.code) {
            this.selectPaymentOption(option, true)
          }
        }
      }
    },
    async validate (showErrors) {
      this.errors = []
      const errors = []

      let scrollToRef = null

      if (this.response.fields) {
        for (let i = 0; i < this.response.fields.length; i++) {
          const field = this.response.fields[i]

          if (field.custom) {
            if (field.required && !this.submission.customFields[field.customField.id]) {
              errors.push(this.$t('general.fieldIsRequired', { field: field.label }))

              scrollToRef = 'field_' + field.field
            }
          } else if (field.required && !this.submission.fields[field.field]) {
            const key = this.$t('general.' + field.field)
            errors.push(this.$t('general.fieldIsRequired', { field: key }))

            scrollToRef = 'field_' + field.field
          }
        }
      }

      if (this.response.terms.required && !this.submission.termsAccepted) {
        errors.push(this.$t('general.fieldIsRequired', { field: this.response.terms.title }))

        scrollToRef = 'terms'
      }

      if (errors.length > 0) {
        if (showErrors) {
          this.errors = errors

          if (scrollToRef) {
            this.$nextTick(() => {
              // this.scrollTo(this.$refs[scrollToRef].$el)
            })
          }
        }

        return false
      }

      try {
        const response = await this.$api.booking.confirm(this.$api.booking.ACTION_VALIDATE, this.selectedCustomer, this.selectedSlot, { moveReservation: this.moveReservation, rebookReservation: this.rebookReservation, voucher: this.submission.voucher, giftCards: this.submission.payment.giftCards, termsAccepted: this.submission.termsAccepted, fields: this.submission.fields, customFields: this.submission.customFields, waitlist: this.waitlist })

        if (response.success) {
          return true
        } else {
          if (showErrors) {
            for (let i = 0; i < response.errors.length; i++) {
              this.errors.push(response.errors[i])
            }
          }

          // this.$sentry.captureException(this.errors)

          return false
        }
      } catch (error) {
        console.log(error)
        this.errors.push(this.$t('general.errorBooking'))
        //  this.$sentry.captureException(error)
        return false
      }
    },
    async submit () {
      this.submitting = true

      this.errors = []

      let scrollToRef = null

      if (this.response.fields) {
        for (let i = 0; i < this.response.fields.length; i++) {
          const field = this.response.fields[i]

          if (field.custom) {
            if (field.required && !this.submission.customFields[field.customField.id]) {
              this.errors.push(this.$t('general.fieldIsRequired', { field: field.label }))

              scrollToRef = 'field_' + field.field
            }
          } else if (field.required && !this.submission.fields[field.field]) {
            const key = this.$t('general.' + field.field)
            this.errors.push(this.$t('general.fieldIsRequired', { field: key }))

            scrollToRef = 'field_' + field.field
          }
        }
      }

      if (this.response.terms.required && !this.submission.termsAccepted) {
        this.errors.push(this.$t('general.fieldIsRequired', { field: this.response.consentTitle }))

        scrollToRef = 'terms'
      }

      if (this.errors.length > 0) {
        this.submitting = false

        if (scrollToRef) {
          this.$nextTick(() => {
            // this.scrollTo(this.$refs[scrollToRef].$el)
          })
        }


        // this.$sentry.captureException(this.errors)

        return false
      }

      try {
        const rwg_token = useCookie('_rwg_token').value

        const response = await this.$api.booking.confirm(this.$api.ACTION_CONFIRM, this.selectedCustomer, this.selectedSlot, {
          moveReservation: this.moveReservation,
          rebookReservation: this.rebookReservation,
          voucher: this.submission.voucher,
          giftCards: this.submission.payment.giftCards,
          termsAccepted: this.submission.termsAccepted,
          fields: this.submission.fields,
          customFields: this.submission.customFields,
          paymentData: this.submission.payment.data,
          waitlist: this.waitlist,
          rwg_token
        })

        if (response.success) {
          const reservation = response.reservation
          const action = (this.moveReservation) ? 'move' : 'book'

          this.$router.push({ path: '/book-now/complete', query: { action, id: reservation.id, date: reservation.date, customer: reservation.customer.id, signature: reservation.signature } })
        } else {
          this.submitting = false

          for (let i = 0; i < response.errors.length; i++) {
            this.errors.push(response.errors[i])
          }
        }
      } catch (error) {
        console.log(error)
        this.submitting = false
        this.errors.push(this.$t('general.errorBooking'))
        // this.$sentry.captureException(error)
      }

      // this.$sentry.captureException(this.errors)
    },
    selectFirstPaymentType () {
      if (!this.response.payment || !this.response.payment.options) { return }
      if (this.selectedPaymentOption !== null) { return }

      this.selectPaymentOption(this.response.payment.options[0], false)
    },
    async selectPaymentOption (option, showErrors) {
      if (this.selectedPaymentOption === option) { return }

      if (!option) {
        this.selectedPaymentOption = null
        return
      }

      this.selectedPaymentOption = null
      this.selectingPaymentOptionCode = option.code

      let valid = this.submission.locked
      if (!valid) { valid = await this.validate(showErrors) }

      if (valid) {
        this.submission.locked = true

        if (!option.methods) {
          this.submission.payment.data = option.data
        }

        this.$nextTick(() => {
          this.selectedPaymentOption = option
        })
      } else {
        this.selectPaymentOption(null, showErrors)
      }

      this.selectingPaymentOptionCode = null
    },
    externalFallbackRouteCallback (method) {
      const bookingUrlQuery = this.$store.getters['booking/urlQuery']

      const submission = { ...this.submission }
      submission.payment.data = method.data

      return {
        path: '/book-now/confirm',
        query: {
          slot: bookingUrlQuery.slot,
          customer: bookingUrlQuery.customer,
          move: bookingUrlQuery.move,
          rebook: bookingUrlQuery.rebook,
          submission: Buffer.from(JSON.stringify(submission)).toString('base64')
        }
      }
    },
    actionCallback (method, params) {
      return this.$api.booking.confirm(this.$api.ACTION_INTEGRATION, this.selectedCustomer, this.selectedSlot, { moveReservation: this.moveReservation, rebookReservation: this.rebookReservation, voucher: this.submission.voucher, giftCards: this.submission.payment.giftCards, termsAccepted: this.submission.termsAccepted, fields: this.submission.fields, customFields: this.submission.customFields, paymentData: method.data, paymentParams: params, waitlist: this.waitlist })
    },
    async paymentCallback (method, { success, params, errors }) {
      if (success) {
        this.processingPayment = true
        try {
          const rwg_token = useCookie('_rwg_token').value

          const response = await this.$api.booking.confirm(this.$api.ACTION_CONFIRM, this.selectedCustomer, this.selectedSlot, {
            moveReservation: this.moveReservation,
            rebookReservation: this.rebookReservation,
            voucher: this.submission.voucher,
            giftCards: this.submission.payment.giftCards,
            termsAccepted: this.submission.termsAccepted,
            fields: this.submission.fields,
            customFields: this.submission.customFields,
            paymentData: method.data,
            paymentParams: params,
            waitlist: this.waitlist,
            rwg_token
          })

          if (response.success) {
            const reservation = response.reservation

            this.$router.push({ path: '/book-now/complete', query: { id: reservation.id, date: reservation.date, customer: reservation.customer.id, signature: reservation.signature } })
          } else {
            this.processingPayment = false
            this.errors = response.errors
          }
        } catch (error) {
          console.log(error)
          this.processingPayment = false
          this.errors.push(this.$t('general.errorBooking'))
          // this.$sentry.captureException(error)
        }
      } else {
        this.errors = errors
      }
    }
  }
})
</script>

<style>
.confirmation-details--services--service {
  font-weight: bold;
  font-size: 16px;
}

.confirmation-details--services--employee {
  color: var(--bookapp-content-text-color) !important;
  font-size: 13px;
}

.confirmation-details--customer {
  font-size: 14px;
  margin-bottom:10px;
}

.confirmation-details--customer strong {
  font-size: 16px;
}

.confirmation-details--services--price {
  font-size: 14px;
}

.confirmation-details--date {
  font-size: 14px;
  margin-bottom:20px;
}

.payment-option-card {
  flex-grow: 1;
  height: 100%;
  cursor: pointer;
}
</style>
